import React, { useCallback, useEffect, useRef, useState } from 'react';
import { dateTime24 } from '../../utils/time24.js';

/**
 * 9030: the captain takes a photo of themself when finalising the Statement of
 * Facts. A live front-camera preview where the browser allows it, otherwise the
 * phone's camera through a file input (capture="user"). The photo is downscaled
 * and compressed on the device before upload.
 */

const MAX_SIDE = 800;
const QUALITY = 0.7;

/** Draw a video frame or image onto a canvas no larger than MAX_SIDE, as a JPEG data URL. */
function toJpeg(source, width, height) {
  const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d');
  // JPEG has no transparency: paint white under the photo.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', QUALITY);
}

function fileToJpeg(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        resolve(toJpeg(img, img.naturalWidth, img.naturalHeight));
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file is not a photo this device can read.'));
    };
    img.src = url;
  });
}

const canStream = () => typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

export default function CaptainPhoto({ value, onChange }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [live, setLive] = useState(false);
  const [error, setError] = useState('');

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setLive(false);
  }, []);

  useEffect(() => stopCamera, [stopCamera]);

  useEffect(() => {
    if (live && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [live]);

  const openCamera = async () => {
    setError('');
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 } },
        audio: false,
      });
      setLive(true);
    } catch {
      // No camera permission (or none at all): fall back to the phone's camera app.
      setError('The camera could not be opened here. Use "Take / choose photo" instead.');
      fileRef.current?.click();
    }
  };

  const snap = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) {
      setError('The camera is still starting. Try again in a moment.');
      return;
    }
    onChange({ photo: toJpeg(v, v.videoWidth, v.videoHeight), takenAt: new Date().toISOString() });
    stopCamera();
  };

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    try {
      onChange({ photo: await fileToJpeg(file), takenAt: new Date().toISOString() });
    } catch (err) {
      setError(err.message || 'Could not read that photo.');
    }
  };

  const retake = () => {
    onChange(null);
    setError('');
  };

  return (
    <div className="sof-captain-photo" data-testid="sof-captain-photo-capture">
      {value?.photo ? (
        <div className="d-flex align-items-start gap-3">
          <img
            src={value.photo}
            alt="Captain's photo for this Statement of Facts"
            className="sof-photo-img"
            data-testid="sof-captain-photo-preview"
          />
          <div className="small">
            <div className="text-success fw-bold">
              <i className="fas fa-check me-1" aria-hidden="true" />
              Photo taken
            </div>
            <div className="text-muted" data-testid="sof-captain-photo-time">
              {dateTime24(value.takenAt)}
            </div>
            <button type="button" className="btn btn-link btn-sm px-0" onClick={retake} data-testid="sof-captain-photo-retake">
              Retake
            </button>
          </div>
        </div>
      ) : live ? (
        <div>
          <video ref={videoRef} className="sof-photo-video" playsInline muted data-testid="sof-captain-photo-video" />
          <div className="d-flex gap-2 mt-2">
            <button type="button" className="btn btn-primary btn-sm" onClick={snap} data-testid="sof-captain-photo-snap">
              <i className="fas fa-camera me-1" aria-hidden="true" />
              Take Photo
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={stopCamera}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-wrap gap-2">
          {canStream() && (
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={openCamera} data-testid="sof-captain-photo-camera">
              <i className="fas fa-video me-1" aria-hidden="true" />
              Open Camera
            </button>
          )}
          <label className="btn btn-outline-secondary btn-sm mb-0" htmlFor="sof-captain-photo-file">
            <i className="fas fa-camera me-1" aria-hidden="true" />
            Take / choose photo
          </label>
        </div>
      )}
      {/* Always mounted so the fallback (and tests) can use it. */}
      <input
        ref={fileRef}
        id="sof-captain-photo-file"
        type="file"
        accept="image/*"
        capture="user"
        className="visually-hidden"
        onChange={pickFile}
        data-testid="sof-captain-photo-input"
      />
      {error && (
        <div className="small text-danger mt-1" role="alert" data-testid="sof-captain-photo-error">
          {error}
        </div>
      )}
    </div>
  );
}
